"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useTheme } from "next-themes"
import { useNativeStatusBar } from "@/components/native/native-status-bar-context"
import { useStatusBarChrome } from "@/components/native/status-bar-chrome-context"
import { isCapacitorNative } from "@/lib/capacitor"

/** Radix modal content sets role + data-state; catches overlays if the open counter desyncs. */
function queryRadixOpenModalPresent(): boolean {
  if (typeof document === "undefined") return false
  return Boolean(
    document.querySelector(
      '[data-app-modal="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
    )
  )
}

function useRadixOpenModalInDom(modalOverlayDepth: number): boolean {
  const [open, setOpen] = useState(false)

  useLayoutEffect(() => {
    if (!isCapacitorNative()) return undefined

    const sync = () => setOpen(queryRadixOpenModalPresent())
    sync()
    const mo = new MutationObserver(() => sync())
    mo.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-state"],
    })
    return () => mo.disconnect()
  }, [])

  useLayoutEffect(() => {
    if (!isCapacitorNative()) return
    setOpen(queryRadixOpenModalPresent())
  }, [modalOverlayDepth])

  return open
}

/**
 * @capacitor/status-bar Style enum (string sent to native):
 * - Style.Dark  ("DARK")  = light-colored status bar content (for dark backdrops)
 * - Style.Light ("LIGHT") = dark-colored status bar content (for light backdrops)
 */
export function StatusBarManager() {
  const { resolvedTheme } = useTheme()
  const { modalOverlayDepth } = useNativeStatusBar()
  const radixModalInDom = useRadixOpenModalInDom(modalOverlayDepth)
  const { statusBarOverDarkHeader } = useStatusBarChrome()
  const timeoutIds = useRef<number[]>([])
  /** Prefer counter; DOM matches Radix dialog/sheet/alert/vaul when counter misses an open state. */
  const modalOpen = modalOverlayDepth > 0 || radixModalInDom

  useEffect(() => {
    if (!isCapacitorNative()) return
    if (!resolvedTheme) return

    let cancelled = false

    const run = async () => {
      if (cancelled) return
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar")
        const style = pickStatusBarStyle(Style, resolvedTheme, modalOpen, statusBarOverDarkHeader)
        await StatusBar.setOverlaysWebView({ overlay: true })
        await StatusBar.setBackgroundColor({ color: "#00000000" })
        await StatusBar.setStyle({ style })
      } catch {
        const cap = window.Capacitor
        const statusBar = cap?.Plugins?.StatusBar
        if (!statusBar) return
        const platform = cap?.getPlatform?.()
        const style = pickStatusBarStyleLegacy(resolvedTheme, modalOpen, statusBarOverDarkHeader)
        if (platform === "android") {
          await statusBar.setOverlaysWebView({ overlay: true })
          await statusBar.setBackgroundColor({ color: "#00000000" })
          await statusBar.setStyle({ style })
        } else if (platform === "ios") {
          await statusBar.setOverlaysWebView({ overlay: true })
          await statusBar.setStyle({ style })
        }
      }
    }

    void run()

    timeoutIds.current.forEach((id) => window.clearTimeout(id))
    timeoutIds.current = []
    if (modalOpen) {
      const delays = [0, 16, 50, 120, 250, 400]
      delays.forEach((ms) => {
        const id = window.setTimeout(() => void run(), ms)
        timeoutIds.current.push(id)
      })
    }

    return () => {
      cancelled = true
      timeoutIds.current.forEach((id) => window.clearTimeout(id))
      timeoutIds.current = []
    }
  }, [resolvedTheme, modalOpen, statusBarOverDarkHeader])

  return null
}

function pickStatusBarStyle(
  Style: typeof import("@capacitor/status-bar").Style,
  resolvedTheme: string,
  modalOpen: boolean,
  overDarkHeader: boolean
) {
  // Modal: theme only (header chrome is covered; status bar reads app background).
  if (modalOpen) {
    return resolvedTheme === "dark" ? Style.Dark : Style.Light
  }
  // Dashboard (etc.): dark header under status bar → light icons.
  if (overDarkHeader) {
    return Style.Dark
  }
  return resolvedTheme === "dark" ? Style.Dark : Style.Light
}

function pickStatusBarStyleLegacy(
  resolvedTheme: string,
  modalOpen: boolean,
  overDarkHeader: boolean
): "LIGHT" | "DARK" {
  if (modalOpen) {
    return resolvedTheme === "dark" ? "DARK" : "LIGHT"
  }
  if (overDarkHeader) {
    return "DARK"
  }
  return resolvedTheme === "dark" ? "DARK" : "LIGHT"
}
